import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';
import InviteFamilyModal from '../components/InviteFamilyModal';

const Clients = ({ user, onLogout }) => {
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [inviteClient, setInviteClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    county: '',
    state: '',
    age: '',
    phone: '',
    phone_alt1: '',
    email: '',
    email_alt1: '',
    health_condition: '',
    emergency_contacts: '',
    history: '',
    special_instructions: ''
  });

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      county: '',
      state: '',
      age: '',
      phone: '',
      phone_alt1: '',
      email: '',
      email_alt1: '',
      health_condition: '',
      emergency_contacts: '',
      history: '',
      special_instructions: ''
    });
    setEditingClient(null);
    setShowAddForm(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null
      };

      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, dataToSubmit);
        toast.success('Client updated successfully');
      } else {
        await axios.post(`${API}/clients`, dataToSubmit);
        toast.success('Client added successfully');
      }
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(editingClient ? 'Failed to update client' : 'Failed to add client');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      address: client.address || '',
      county: client.county || '',
      state: client.state || '',
      age: client.age || '',
      phone: client.phone || '',
      phone_alt1: client.phone_alt1 || '',
      email: client.email || '',
      email_alt1: client.email_alt1 || '',
      health_condition: client.health_condition || '',
      emergency_contacts: client.emergency_contacts || '',
      history: client.history || '',
      special_instructions: client.special_instructions || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await axios.delete(`${API}/clients/${clientId}`);
        toast.success('Client deleted');
        fetchClients();
      } catch (error) {
        toast.error('Failed to delete client');
      }
    }
  };

  const handleAvatarUpload = async (clientId, file) => {
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    try {
      await axios.post(`${API}/clients/${clientId}/avatar`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo updated!');
      fetchClients();
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  return (
    <div className="dashboard-container" data-testid="clients-page">
      <NavBar onLogout={onLogout} />

      <div className="dashboard-content page-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1 className="page-title" style={{ marginBottom: 0 }}>My Clients</h1>
          <button 
            className="submit-button" 
            style={{ width: 'auto', padding: '0.75rem 1.5rem' }}
            onClick={() => {
              if (showAddForm) {
                resetForm();
              } else {
                setShowAddForm(true);
              }
            }}
            data-testid="add-client-toggle"
          >
            {showAddForm ? 'Cancel' : '+ Add Client'}
          </button>
        </div>

        {showAddForm && (
          <div className="section-card" style={{ marginBottom: '2rem' }} data-testid="client-form">
            <h2 className="section-title">{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                <div className="form-group">
                  <label htmlFor="name">Client Name *</label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Full name"
                    data-testid="client-name-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="age">Age</label>
                  <input
                    id="age"
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="1"
                    max="120"
                    placeholder="Age"
                    data-testid="client-age-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Primary Phone</label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    data-testid="client-phone-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone_alt1">Alternate Phone</label>
                  <input
                    id="phone_alt1"
                    type="tel"
                    name="phone_alt1"
                    value={formData.phone_alt1}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    data-testid="client-phone-alt-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Primary Email</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    data-testid="client-email-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email_alt1">Alternate Email</label>
                  <input
                    id="email_alt1"
                    type="email"
                    name="email_alt1"
                    value={formData.email_alt1}
                    onChange={handleChange}
                    placeholder="alternate@example.com"
                    data-testid="client-email-alt-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  rows="2"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Full address for emergency responders"
                  data-testid="client-address-input"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div className="form-group">
                  <label htmlFor="county">County</label>
                  <input
                    id="county"
                    type="text"
                    name="county"
                    value={formData.county}
                    onChange={handleChange}
                    placeholder="e.g., Gwinnett County"
                    data-testid="client-county-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input
                    id="state"
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="e.g., Georgia"
                    data-testid="client-state-input"
                  />
                </div>
              </div>
              <p style={{ color: '#6b8899', fontSize: '0.85rem', marginTop: '0.5rem', marginBottom: '1rem' }}>
                📍 Location is used to show local resources for this client on the Resources page
              </p>

              <div className="form-group">
                <label htmlFor="health_condition">Health Condition / Diagnosis</label>
                <textarea
                  id="health_condition"
                  name="health_condition"
                  rows="3"
                  value={formData.health_condition}
                  onChange={handleChange}
                  placeholder="Alzheimer's stage, dementia type, medications, allergies..."
                  data-testid="client-health-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="emergency_contacts">Client's Emergency Contacts</label>
                <textarea
                  id="emergency_contacts"
                  name="emergency_contacts"
                  rows="3"
                  value={formData.emergency_contacts}
                  onChange={handleChange}
                  placeholder="Family members, doctors, etc.
Example: Son John - 555-1234"
                  data-testid="client-emergency-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="history">Medical History & Care Notes</label>
                <textarea
                  id="history"
                  name="history"
                  rows="4"
                  value={formData.history}
                  onChange={handleChange}
                  placeholder="Diagnosis date, progression, behavioral patterns, daily routine..."
                  data-testid="client-history-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="special_instructions">Special Instructions</label>
                <textarea
                  id="special_instructions"
                  name="special_instructions"
                  rows="3"
                  value={formData.special_instructions}
                  onChange={handleChange}
                  placeholder="Important information for emergency responders..."
                  data-testid="client-instructions-input"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="submit-button" data-testid="save-client-button">
                  {editingClient ? 'Update Client' : 'Add Client'}
                </button>
                <button 
                  type="button" 
                  className="submit-button" 
                  style={{ background: '#6c757d' }}
                  onClick={resetForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <p>Loading clients...</p>
        ) : clients.length === 0 ? (
          <div className="section-card">
            <div className="empty-state" data-testid="empty-clients">
              <div className="empty-state-icon">👥</div>
              <h3>No Clients Added Yet</h3>
              <p style={{ color: '#6b8899' }}>
                Add clients you are caring for to keep track of their information and ensure their safety.
              </p>
              <button 
                className="submit-button" 
                style={{ marginTop: '1rem', width: 'auto', padding: '0.75rem 2rem' }}
                onClick={() => setShowAddForm(true)}
              >
                Add Your First Client
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {clients.map((client) => (
              <div key={client.id} className="section-card" data-testid={`client-card-${client.id}`}>
                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
                  {/* Avatar Section */}
                  <div className="avatar-upload-section" style={{ minWidth: '120px' }}>
                    <div className="avatar-preview" style={{ width: '100px', height: '100px' }}>
                      {client.avatar ? (
                        <img src={client.avatar} alt={client.name} />
                      ) : (
                        <span style={{ fontSize: '2rem' }}>👤</span>
                      )}
                    </div>
                    <input
                      type="file"
                      id={`avatar-${client.id}`}
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleAvatarUpload(client.id, e.target.files[0]);
                        }
                      }}
                    />
                    <label 
                      htmlFor={`avatar-${client.id}`} 
                      className="avatar-upload-btn"
                      style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                    >
                      📸 Photo
                    </label>
                  </div>

                  {/* Info Section */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div>
                        <h3 style={{ margin: 0, color: '#2c5364', fontSize: '1.3rem' }}>{client.name}</h3>
                        {client.age && <span style={{ color: '#6b8899' }}>Age: {client.age}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="submit-button"
                          style={{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                          onClick={() => handleEdit(client)}
                          data-testid={`edit-client-${client.id}`}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDelete(client.id)}
                          data-testid={`delete-client-${client.id}`}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>

                    {/* Daily Care Log CTA row */}
                    <div className="client-carelog-cta" data-testid={`carelog-cta-${client.id}`}>
                      <Link
                        to={`/clients/${client.id}/care-logs/new`}
                        className="client-carelog-btn primary"
                        data-testid={`start-carelog-${client.id}`}
                      >
                        <i className="fa-solid fa-clipboard-check" /> Start Daily Care Log
                      </Link>
                      <Link
                        to={`/clients/${client.id}/care-logs/new?prefill=last`}
                        className="client-carelog-btn quick"
                        data-testid={`quick-log-${client.id}`}
                        title="Prefill from last log — great for stable clients"
                      >
                        <i className="fa-solid fa-bolt" /> Quick Log
                      </Link>
                      <Link
                        to={`/clients/${client.id}/care-logs`}
                        className="client-carelog-btn secondary"
                        data-testid={`view-carelogs-${client.id}`}
                      >
                        <i className="fa-solid fa-clipboard-list" /> View Previous Logs
                      </Link>
                      <Link
                        to={`/clients/${client.id}/care-plan`}
                        className="client-carelog-btn tertiary"
                        data-testid={`care-plan-${client.id}`}
                      >
                        <i className="fa-solid fa-clipboard-user" /> Care Plan
                      </Link>
                      <button
                        onClick={() => setInviteClient(client)}
                        className="client-carelog-btn invite"
                        data-testid={`invite-family-${client.id}`}
                      >
                        <i className="fa-solid fa-envelope-open-text" /> Invite Family
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                      {client.address && (
                        <div>
                          <strong style={{ color: '#2c5364' }}>📍 Address:</strong>
                          <p style={{ margin: '0.25rem 0', color: '#4a6572' }}>{client.address}</p>
                        </div>
                      )}
                      {(client.phone || client.phone_alt1) && (
                        <div>
                          <strong style={{ color: '#2c5364' }}>📞 Phone:</strong>
                          <p style={{ margin: '0.25rem 0', color: '#4a6572' }}>
                            {client.phone}
                            {client.phone_alt1 && <><br />{client.phone_alt1} (alt)</>}
                          </p>
                        </div>
                      )}
                      {(client.email || client.email_alt1) && (
                        <div>
                          <strong style={{ color: '#2c5364' }}>📧 Email:</strong>
                          <p style={{ margin: '0.25rem 0', color: '#4a6572' }}>
                            {client.email}
                            {client.email_alt1 && <><br />{client.email_alt1} (alt)</>}
                          </p>
                        </div>
                      )}
                    </div>

                    {client.health_condition && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#e7f3ff', borderRadius: '8px' }}>
                        <strong style={{ color: '#0c5460' }}>🏥 Health Condition:</strong>
                        <p style={{ margin: '0.5rem 0 0', color: '#0c5460' }}>{client.health_condition}</p>
                      </div>
                    )}

                    {client.special_instructions && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#fff3cd', borderRadius: '8px' }}>
                        <strong style={{ color: '#856404' }}>⚠️ Special Instructions:</strong>
                        <p style={{ margin: '0.5rem 0 0', color: '#856404' }}>{client.special_instructions}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {inviteClient && (
        <InviteFamilyModal client={inviteClient} onClose={() => setInviteClient(null)} />
      )}
    </div>
  );
};

export default Clients;
